import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [totpRequired, setTotpRequired] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const result = await login(email, password, totpRequired ? totpCode : undefined);

    if (result.success) {
      toast.success("Connexion réussie !");
      navigate("/dashboard");
    } else if (result.requireTotp) {
      setTotpRequired(true);
      setError("");
      toast.info("Veuillez saisir votre code de vérification à 6 chiffres.");
    } else {
      // If the server rejected the TOTP code, keep the TOTP field visible
      if (!totpRequired) setTotpRequired(false);
      setError(result.error);
    }

    setLoading(false);
  };

  const resetTotpFlow = () => {
    setTotpRequired(false);
    setTotpCode("");
    setError("");
  };

  return (
    <div className="min-h-screen bg-slate-50 font-['DM_Sans'] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-hostprotect.png" alt="HostProtect" className="h-9 w-auto" />
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center py-12 px-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-lg w-full max-w-md" data-testid="login-card">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-[#19294C] font-['Outfit']">
              Connexion
            </h1>
            <p className="mt-2 text-slate-600">
              Accédez à votre espace HostProtect
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {!totpRequired && (
              <>
                <div>
                  <Label htmlFor="email" className="text-slate-700">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="votre@email.com"
                      className="pl-10 border-slate-300 focus:ring-[#00B8E4] focus:border-[#00B8E4]"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center">
                    <Label htmlFor="password" className="text-slate-700">Mot de passe</Label>
                    <Link
                      to="/mot-de-passe-oublie"
                      className="text-sm text-[#00B8E4] hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Mot de passe oublié ?
                    </Link>
                  </div>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 pr-10 border-slate-300 focus:ring-[#00B8E4] focus:border-[#00B8E4]"
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </>
            )}

            {totpRequired && (
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                  <ShieldCheck className="w-5 h-5 text-[#00B8E4] flex-shrink-0" />
                  <p className="text-sm text-slate-700">
                    Votre compte est protégé par une authentification à deux facteurs. Saisissez le code à 6 chiffres affiché dans votre application (Google Authenticator, 1Password, etc.).
                  </p>
                </div>
                <div>
                  <Label htmlFor="totp" className="text-slate-700">Code de vérification</Label>
                  <Input
                    id="totp"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    className="mt-1 text-center text-2xl tracking-[0.5em] font-mono border-slate-300 focus:ring-[#00B8E4] focus:border-[#00B8E4]"
                    required
                    autoFocus
                    data-testid="login-totp-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={resetTotpFlow}
                  className="text-sm text-slate-500 hover:text-slate-700 underline"
                >
                  Utiliser un autre compte
                </button>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || (totpRequired && totpCode.length !== 6)}
              className="w-full bg-[#00B8E4] hover:bg-[#00a5ce] text-white py-3 font-semibold"
              data-testid="login-submit-btn"
            >
              {loading
                ? "Connexion..."
                : totpRequired
                ? "Vérifier le code"
                : "Se connecter"}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-600 mt-6">
            Vous n'avez pas de compte ?{" "}
            <Link to="/profil" className="text-[#00B8E4] hover:underline font-medium">
              Créer un compte
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
